import React, { useMemo, useState, createElement } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { TransactionFilters, FilterState } from '../components/transactions/TransactionFilters';
import { TransactionTable } from '../components/transactions/TransactionTable';
import { MOCK_TRANSACTIONS } from '../lib/mockData';
import { formatCurrency } from '../lib/utils';
import { Search, Download, DollarSign, Receipt } from 'lucide-react';
export function Transactions() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      from: '',
      to: ''
    },
    status: [],
    amountRange: {
      min: '',
      max: ''
    },
    locationId: ''
  });
  // Filter Logic
  const filteredData = useMemo(() => {
    return MOCK_TRANSACTIONS.filter(txn => {
      // Search Query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!txn.id.toLowerCase().includes(query) && !txn.customerHash.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Date Range
      if (filters.dateRange.from && new Date(txn.date) < new Date(filters.dateRange.from)) return false;
      if (filters.dateRange.to) {
        const toDate = new Date(filters.dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        if (new Date(txn.date) > toDate) return false;
      }
      // Status
      if (filters.status.length > 0 && !filters.status.includes(txn.status)) return false;
      // Amount Range
      if (filters.amountRange.min && txn.amount < Number(filters.amountRange.min)) return false;
      if (filters.amountRange.max && txn.amount > Number(filters.amountRange.max)) return false;
      // Location
      if (filters.locationId && txn.locationId !== filters.locationId) return false;
      return true;
    });
  }, [searchQuery, filters]);
  // Summary Stats
  const stats = useMemo(() => {
    return filteredData.reduce((acc, curr) => ({
      count: acc.count + 1,
      sales: acc.sales + curr.amount
    }), {
      count: 0,
      sales: 0
    });
  }, [filteredData]);
  const handleExport = () => {
    const headers = ['Date', 'Transaction ID', 'Customer ID', 'Amount', 'Cashback', 'Status', 'Location'];
    const csvContent = [headers.join(','), ...filteredData.map(txn => [new Date(txn.date).toLocaleDateString(), txn.id, txn.customerHash, txn.amount.toFixed(2), txn.cashback.toFixed(2), txn.status, txn.locationId].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Kithward_Transactions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  return <div className="space-y-8" data-id="element-635">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-636">
        <div data-id="element-637">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-638">
            Transactions
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-639">
            View and manage all your transaction history.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} data-id="element-640">
          <Download className="h-4 w-4 mr-2" data-id="element-641" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2" data-id="element-642">
        <Card data-id="element-643">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-644">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-645">
              Total Transactions
            </CardTitle>
            <Receipt className="h-4 w-4 text-slate-400" data-id="element-646" />
          </CardHeader>
          <CardContent data-id="element-647">
            <div className="text-2xl font-bold" data-id="element-648">{stats.count}</div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-649">Filtered results</p>
          </CardContent>
        </Card>
        <Card data-id="element-650">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-651">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-652">
              Total Sales
            </CardTitle>
            <DollarSign className="h-4 w-4 text-slate-400" data-id="element-653" />
          </CardHeader>
          <CardContent data-id="element-654">
            <div className="text-2xl font-bold" data-id="element-655">
              {formatCurrency(stats.sales)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-656">
              Gross transaction volume
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4" data-id="element-657">
        <div className="relative" data-id="element-658">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-659" />
          <Input placeholder="Search by transaction ID or customer ID..." className="pl-10 max-w-md bg-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-660" />
        </div>

        <TransactionFilters filters={filters} onFilterChange={setFilters} onClearFilters={() => setFilters({
        dateRange: {
          from: '',
          to: ''
        },
        status: [],
        amountRange: {
          min: '',
          max: ''
        },
        locationId: ''
      })} data-id="element-661" />
      </div>

      {/* Table */}
      <TransactionTable data={filteredData} data-id="element-662" />
    </div>;
}